<template>
  <b-card no-body>
    <template #header v-if="!hiddenHeader">
      <div class="font-weight-600">Histórico de Tentativas de Pagamentos</div>
    </template>

    <b-table
      striped
      hover
      responsive
      show-empty
      :busy="loading"
      :items="items"
      :fields="fields"
    >
      <template #cell(id)="{ value }">
        <span class="text-monospace">{{ value || "-" }}</span>
      </template>

      <template #cell(gateway_payment_id)="{ value, item }">
        <a
          v-if="item?.link_asaas"
          target="_blank"
          class="text-"
          :href="item?.link_asaas"
        >
          <span class="text-monospace pr-1">{{ value || "-" }}</span>
          <font-awesome-icon icon="arrow-up-right-from-square" size="sm" />
        </a>
        <span v-else class="text-monospace">{{ value || "-" }}</span>
      </template>

      <template #cell(data_pagamento)="{ value }">
        {{ value | format_date }}
      </template>

      <template #cell(created_at)="{ value }">
        {{ value | format_date }}
      </template>

      <template #cell(status)="{ value }">
        <span
          class="font-weight-bolder px-2 lh-1 text-uppercase rounded-sm text-center max-status"
          :class="value?.class"
          v-text="value?.label"
        />
      </template>

      <template #cell(actions)="{ item }">
        <div
          class="d-inline-flex flex-column align-items-center justify-content-end w-100"
        >
          <b-button
            v-if="item.comprovante_url"
            size="sm"
            variant="outline-secondary"
            target="_blank"
            class="mr-0 mb-1 border-0 text-gray-dark"
            :href="item.comprovante_url"
          >
            <font-awesome-icon
              icon="fa-solid fa-paperclip"
              class="text-warning"
            />
            Ver comprovante
          </b-button>
          <b-button
            v-if="item.boleto_url"
            size="sm"
            variant="exitos"
            target="_blank"
            class="mr-0"
            :href="item.boleto_url"
          >
            <font-awesome-icon icon="file-pdf" />
            Baixar boleto
          </b-button>

          <b-button
            v-if="item.nota_fiscal_pdf"
            size="sm"
            variant="exitos"
            target="_blank"
            class="mr-0 mt-1"
            :href="item.nota_fiscal_pdf"
          >
            <font-awesome-icon icon="file-invoice-dollar" />
            Ver NF
          </b-button>

          <b-button
            v-if="item.boleto_linha_digitavel"
            size="sm"
            variant="outline-exitos"
            class="mr-0 mt-1 border-0"
            :disabled="clipboardPending"
            @click="copyText(item.boleto_linha_digitavel)"
          >
            <font-awesome-icon icon="copy" />
            Copiar linha
          </b-button>

          <b-button
            v-if="hasUpdateError(item)"
            size="sm"
            variant="outline-danger"
            class="mt-1"
            v-b-tooltip.hover
            title="Visualizar erro"
            label="Visualizar erro"
            @click="showUpdateError(item.mensagem_atualizacao)"
          >
            <font-awesome-icon icon="circle-exclamation" />
            Ver erro
          </b-button>
        </div>
      </template>

      <template #empty>
        <h5 class="text-center">Nenhum pagamento encontrado</h5>
      </template>

      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle" />
        </div>
      </template>
    </b-table>

    <b-modal
      v-model="errorModalOpened"
      centered
      scrollable
      ok-only
      ok-title="Fechar"
      ok-variant="exitos"
      title="Erro na atualização"
    >
      <p class="mb-0 update-error-message">
        {{ selectedErrorMessage }}
      </p>
    </b-modal>
  </b-card>
</template>

<script>
import numberFormat from "@/helpers/filters/number_format";
import { toUpper } from "@/helpers/functions/StringHelpers";
import { clipboard } from "@/mixins/clipboard";
import {
  enumMap,
  getParsedStatus,
} from "@/modules/pedidos/Components/getParsedStatus";

export default {
  name: "TableHistory",
  mixins: [clipboard()],
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hiddenHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      errorModalOpened: false,
      selectedErrorMessage: "",
    };
  },
  computed: {
    fields() {
      return [
        {
          key: "id",
          label: "ID Pagam.",
          class: "align-middle text-center col-1",
        },
        {
          key: "gateway_payment_id",
          label: "Gateway ID",
          class: "align-middle",
        },
        {
          key: "valor",
          label: "Valor",
          class: "align-middle",
          formatter: (value) => this.formatCurrency(value),
        },
        {
          key: "data_pagamento",
          label: "Data Pagamento",
          class: "align-middle",
        },
        {
          key: "created_at",
          label: "Criado em",
          class: "align-middle",
        },
        {
          key: "status",
          label: "Status",
          class: "align-middle text-center",
          formatter: (value) => getParsedStatus(this.getStatusKey(value)),
        },
        {
          key: "actions",
          label: "",
          class: "align-middle col-2",
        },
      ];
    },
  },
  methods: {
    formatCurrency(value) {
      if (value === null || value === undefined || value === "") return "-";

      return `R$ ${numberFormat(Number(value))}`;
    },
    formatDate(value) {
      if (!value) return "-";

      return this.$options.filters.format_date(value, "DD/MM/YYYY");
    },
    formatDateTime(value) {
      if (!value) return "-";

      return this.$options.filters.format_date(value, "DD/MM/YYYY HH:mm");
    },
    getParsedStatus(status) {
      return getParsedStatus(status);
    },
    getStatusKey(status) {
      const statusKey = toUpper(status || "");

      return enumMap[statusKey];
    },
    hasUpdateError(item) {
      const statusKey = this.getStatusKey(item?.status);

      return Boolean(item?.mensagem_atualizacao) && statusKey === enumMap.ERRO;
    },
    showUpdateError(message) {
      this.selectedErrorMessage = message || "";
      this.errorModalOpened = true;
    },
    async copyText(text) {
      try {
        await this.copyToClipboard(text);
      } catch (error) {
        console.log("error", error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.max-status {
  display: inline-block;
  max-width: fit-content;
}

.table-actions {
  min-width: 76px;
}

::v-deep .bg-info-custom {
  background-color: #deb10d !important;
}

.update-error-message {
  white-space: pre-wrap;
}
</style>
